import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, Button, Icon } from '@material-ui/core';
import { CatalogForm } from '../../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../../components/CatalogForm/validators';
import { AuthContext } from '../../../../context/authContext';
import ACCESS from '../../../../ACCESS';
import { ClienteService } from '../../../../services/ClienteService';
import AsingnarLP from './AsingnarLP';

const TIPO_CARTA_PORTE_OPTIONS = [
  { value: '1', label: 'Por guía' },
  { value: '2', label: 'Agrupado' },
  { value: '3', label: 'No Generar' },
];

export const ClienteAdministrativos = ({ match, id: propId, onSave }) => {
  const routeParams = useParams() || {};
  const clientId = propId || match?.params?.id || routeParams?.id;
  const { hasAccess } = useContext(AuthContext);

  const esContado = !hasAccess(ACCESS.CATALOGO_CLIENTES.CREAR_SOLO_CONTADO);

  // Opciones de catálogos
  const [condicionesPago, setCondicionesPago] = useState([]);
  const [dias, setDias] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [facturacionModos, setFacturacionModos] = useState([]);
  const [clienteEstados, setClienteEstados] = useState([]);
  const [gestores, setGestores] = useState([]);

  // Modal para asignación de lista de precios
  const [openLPDialog, setOpenLPDialog] = useState(false);
  const [currentCondicionPago, setCurrentCondicionPago] = useState('1');

  useEffect(() => {
    let mounted = true;
    const fetchCatalogOptions = async () => {
      try {
        const opts = await ClienteService.obtenerCatalogoOptions();
        if (mounted && opts) {
          if (opts.condiciones_pago) {
            setCondicionesPago(
              opts.condiciones_pago.map((c) => ({
                value: String(c.id),
                label: c.condicion_nombre,
              }))
            );
          }
          if (opts.dias) {
            setDias(
              opts.dias.map((d) => ({
                value: String(d.id),
                label: d.dia,
              }))
            );
          }
          if (opts.sucursales) {
            setSucursales(
              opts.sucursales.map((s) => ({
                value: String(s.id),
                label: s.sucursal_nombre,
              }))
            );
          }
          if (opts.facturacion_modos) {
            setFacturacionModos(
              opts.facturacion_modos.map((m) => ({
                value: String(m.id),
                label: m.facturacion_modo,
              }))
            );
          }
          if (opts.cliente_estados) {
            setClienteEstados(
              opts.cliente_estados.map((e) => ({
                value: String(e.id),
                label: e.cliente_estado,
              }))
            );
          }
          if (opts.gestores) {
            setGestores(
              opts.gestores.map((g) => ({
                value: String(g.id),
                label: g.gestor_nombre,
              }))
            );
          }
        }
      } catch (err) {
        console.error('Error al cargar catálogos administrativos', err);
      }
    };
    fetchCatalogOptions();
    return () => {
      mounted = false;
    };
  }, []);

  const loadClientData = useCallback(async () => {
    if (!clientId) return null;
    try {
      const data = await ClienteService.obtenerDetalleCompleto(clientId);
      if (!data) return null;

      setCurrentCondicionPago(data.cliente_id_condicion_pago ? String(data.cliente_id_condicion_pago) : '1');

      return {
        cliente_id_condicion_pago: data.cliente_id_condicion_pago ? String(data.cliente_id_condicion_pago) : '1',
        cliente_limite_credito: data.cliente_limite_credito !== undefined && data.cliente_limite_credito !== null ? String(data.cliente_limite_credito) : '',
        cliente_dias_revision: data.cliente_dias_revision ? String(data.cliente_dias_revision) : '1',
        cliente_dias_pago: data.cliente_dias_pago ? String(data.cliente_dias_pago) : '1',
        cliente_id_oficina_cobra: data.cliente_id_oficina_cobra ? String(data.cliente_id_oficina_cobra) : '',
        cliente_dia_facturacion: data.cliente_dia_facturacion ? String(data.cliente_dia_facturacion) : '1',
        cliente_modo_facturacion: data.cliente_modo_facturacion ? String(data.cliente_modo_facturacion) : '1',
        cliente_id_gestor: data.cliente_id_gestor ? String(data.cliente_id_gestor) : '',
        cliente_mail_copia: Boolean(data.cliente_mail_copia),
        cliente_factura_sin_entrega: Boolean(data.cliente_factura_sin_entrega),
        cliente_obs_facuracion: data.cliente_obs_facuracion || '',
        cliente_digitalizar: Boolean(data.cliente_digitalizar !== undefined ? data.cliente_digitalizar : true),
        cliente_estado: data.cliente_estado ? String(data.cliente_estado) : '1',
        cliente_fecha_suspension: data.cliente_fecha_suspension ? String(data.cliente_fecha_suspension).substring(0, 10) : '',
        cliente_rfc_destrem: Boolean(data.cliente_rfc_destrem),
        cliente_clave_colonia_sat: Boolean(data.cliente_clave_colonia_sat),
        cliente_id_tipo_carta_porte: data.cliente_id_tipo_carta_porte ? String(data.cliente_id_tipo_carta_porte) : '1',
        cliente_pape_factura: Boolean(data.cliente_pape_factura),
        cliente_pape_remision: Boolean(data.cliente_pape_remision),
        cliente_pape_salida: Boolean(data.cliente_pape_salida),
        cliente_pape_tarima: Boolean(data.cliente_pape_tarima),
        cliente_asignacion_directa: Boolean(data.cliente_asignacion_directa),
        cliente_redocumentar: Boolean(data.cliente_redocumentar),
        cliente_cancelar: Boolean(data.cliente_cancelar),
        cliente_requiere_remisiones_entrega: Boolean(data.cliente_requiere_remisiones_entrega),
        cliente_whatsapp_coordinador: data.cliente_whatsapp_coordinador || '',
        cliente_omitir_seguro: Boolean(data.cliente_omitir_seguro),
      };
    } catch (e) {
      console.error('Error al cargar datos administrativos', e);
      return null;
    }
  }, [clientId]);

  const handleCondicionPagoChange = useCallback((val) => {
    const prev = String(currentCondicionPago);
    const next = String(val);
    if ((prev === '8' && next !== '8') || (prev !== '8' && next === '8')) {
      setCurrentCondicionPago(next);
      setOpenLPDialog(true);
    } else {
      setCurrentCondicionPago(next);
    }
  }, [currentCondicionPago]);

  const fields = useMemo(
    () => [
      {
        name: 'section_cobranza',
        label: 'Condiciones Comerciales, Crédito y Cobranza',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_id_condicion_pago',
        label: 'Condición de Pago',
        type: 'select',
        col: 6,
        required: true,
        disabled: esContado,
        validators: [Validators.required('La condición de pago es requerida')],
        options: condicionesPago,
        onChange: handleCondicionPagoChange,
      },
      {
        name: 'cliente_limite_credito',
        label: 'Límite de Crédito',
        type: 'number',
        col: 6,
        placeholder: '$0.00',
      },
      {
        name: 'cliente_dias_revision',
        label: 'Días de Revisión',
        type: 'select',
        col: 3,
        required: true,
        options: dias,
      },
      {
        name: 'cliente_dias_pago',
        label: 'Días de Pago',
        type: 'select',
        col: 3,
        required: true,
        options: dias,
      },
      {
        name: 'cliente_dia_facturacion',
        label: 'Día de Facturación',
        type: 'select',
        col: 3,
        required: true,
        options: dias,
      },
      {
        name: 'cliente_modo_facturacion',
        label: 'Modo de Facturación',
        type: 'select',
        col: 3,
        required: true,
        options: facturacionModos,
      },
      {
        name: 'cliente_id_oficina_cobra',
        label: 'Oficina que Cobra',
        type: 'select',
        col: 6,
        required: true,
        validators: [Validators.required('La oficina de cobro es requerida')],
        options: sucursales,
        placeholder: 'Seleccione sucursal',
      },
      {
        name: 'cliente_id_gestor',
        label: 'Gestor de Cobranza',
        type: 'select',
        col: 6,
        options: gestores,
        placeholder: 'Seleccione gestor si aplica',
      },
      {
        name: 'cliente_estado',
        label: 'Estado del Cliente',
        type: 'select',
        col: 4,
        required: true,
        options: clienteEstados,
      },
      {
        name: 'cliente_fecha_suspension',
        label: 'Fecha de Suspensión',
        type: 'date',
        col: 4,
        disabled: (values) => String(values?.cliente_estado) === '1',
      },
      {
        name: 'cliente_digitalizar',
        label: 'Digitalizar Documentos',
        type: 'checkbox',
        col: 4,
      },
      {
        name: 'cliente_mail_copia',
        label: 'Enviar copia de guía por email',
        type: 'checkbox',
        col: 6,
      },
      {
        name: 'cliente_factura_sin_entrega',
        label: 'Saltar proceso de guía para factura',
        type: 'checkbox',
        col: 6,
      },
      {
        name: 'cliente_rfc_destrem',
        label: 'RFC obligatorio en remitentes y destinatarios',
        type: 'checkbox',
        col: 6,
      },
      {
        name: 'cliente_clave_colonia_sat',
        label: 'Clave SAT obligatoria en remitente y destinatarios',
        type: 'checkbox',
        col: 6,
      },
      {
        name: 'cliente_obs_facuracion',
        label: 'Observaciones de Facturación',
        type: 'textarea',
        col: 12,
        placeholder: 'Instrucciones particulares para la cobranza o emisión de facturas...',
      },
      {
        name: 'section_embarques',
        label: 'Embarques y Carta Porte',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_id_tipo_carta_porte',
        label: 'Tipo de Carta Porte',
        type: 'select',
        col: 6,
        required: true,
        options: TIPO_CARTA_PORTE_OPTIONS,
      },
      {
        name: 'section_papeleria',
        label: 'Papelería Obligatoria',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_pape_factura',
        label: 'Factura',
        type: 'checkbox',
        col: 3,
      },
      {
        name: 'cliente_pape_remision',
        label: 'Remisión',
        type: 'checkbox',
        col: 3,
      },
      {
        name: 'cliente_pape_salida',
        label: 'Orden de Salida',
        type: 'checkbox',
        col: 3,
      },
      {
        name: 'cliente_pape_tarima',
        label: 'Vale de Tarima',
        type: 'checkbox',
        col: 3,
      },
      {
        name: 'section_operacion',
        label: 'Documentación y Permisos Operativos',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_asignacion_directa',
        label: 'Asignación de guías a transportista directa',
        type: 'checkbox',
        col: 4,
      },
      {
        name: 'cliente_redocumentar',
        label: 'Permitir redocumentar guías',
        type: 'checkbox',
        col: 4,
      },
      {
        name: 'cliente_cancelar',
        label: 'Permitir cancelar guías',
        type: 'checkbox',
        col: 4,
      },
      {
        name: 'section_remisiones',
        label: 'Entregas y Remisiones (3M / Manifiestos)',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_requiere_remisiones_entrega',
        label: 'Requiere registro de remisiones en entrega',
        type: 'checkbox',
        col: 6,
      },
      {
        name: 'cliente_whatsapp_coordinador',
        label: 'Teléfono / WhatsApp Coordinador para Alertas',
        type: 'text',
        col: 6,
        placeholder: '10 dígitos con lada',
      },
      {
        name: 'section_reportes',
        label: 'Preferencias de Reportes',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_omitir_seguro',
        label: 'No mostrar Seguro en Reportes',
        type: 'checkbox',
        col: 6,
      },
    ],
    [
      condicionesPago,
      dias,
      sucursales,
      facturacionModos,
      clienteEstados,
      gestores,
      esContado,
      handleCondicionPagoChange,
    ]
  );

  const handleSubmit = useCallback(
    async (values) => {
      const payload = {
        id: clientId,
        ...values,
      };
      return await ClienteService.guardar(payload);
    },
    [clientId]
  );

  const handleSuccess = useCallback(
    (response) => {
      if (onSave) {
        onSave(response);
      }
    },
    [onSave]
  );

  const headerActions = (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      startIcon={<Icon>format_list_bulleted</Icon>}
      onClick={() => setOpenLPDialog(true)}
      className="!capitalize"
    >
      Asignar Listas de Precio
    </Button>
  );

  return (
    <div className="space-y-6">
      <CatalogForm
        title="Datos Administrativos y Cobranza"
        subtitle="Crédito, cobranza, facturación y políticas de embarque"
        fields={fields}
        id={clientId}
        loadData={loadClientData}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        headerActions={headerActions}
        submitButtonText="Guardar Datos Administrativos"
      />

      <Dialog
        open={openLPDialog}
        onClose={() => setOpenLPDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center justify-between">
            <span>Asignar Listas de Precio</span>
            <Button size="small" onClick={() => setOpenLPDialog(false)}>
              <Icon>close</Icon>
            </Button>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <p className="text-sm text-slate-600 mb-4">
            Se asignarán las Listas de Precios correspondientes a la condición de pago y se quitarán las de la condición previa.
          </p>
          <AsingnarLP
            id_cliente={clientId}
            cliente_id_condicion_pago={currentCondicionPago}
            onSave={() => setOpenLPDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default withRouter(ClienteAdministrativos);
